import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Stack,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  SmartToy as RobotIcon,
  Send as SendIcon,
  Close as CloseIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useAssistant } from '../AssistantContext';
import { useFeature } from '../EnvironmentContext';
import { ConversationThread } from './AssistantConversation';

const AssistantChatModal: React.FC = () => {
  const {
    messages,
    loading,
    open,
    collectStatus,
    closeAssistant,
    resetConversation,
    sendMessage,
    sendFeedback,
    startCollect,
    submitCollectAnswer,
    confirmCollectSubmit,
    refreshCollectReview,
    cancelCollect,
  } = useAssistant();
  const collectEnabled = useFeature('Assistant.Collect');
  const navigate = useNavigate();
  const [input, setInput] = React.useState('');

  const submit = () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    if (collectStatus === 'collecting') {
      void submitCollectAnswer(text);
    } else {
      void sendMessage(text);
    }
  };

  const handleConfirmCollect = () => {
    void confirmCollectSubmit().then((id) => {
      if (id) {
        closeAssistant();
        navigate(`/requests/${id}`);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // During a review/hand-off the free-text box is not the collection channel — the user acts via the
  // card buttons — so the box is disabled with a prompt rather than silently feeding guidance chat.
  const awaitingCardAction = collectStatus === 'review' || collectStatus === 'handoff';
  const inputPlaceholder =
    collectStatus === 'collecting'
      ? 'Type your answer…'
      : awaitingCardAction
        ? 'Use the buttons above to continue…'
        : 'Ask AskIAM anything…';

  return (
    <Dialog open={open} onClose={closeAssistant} maxWidth="sm" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <RobotIcon color="primary" />
          <span>AskIAM Assistant</span>
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', right: 8, top: 8 }}>
          {messages.length > 0 && (
            <Tooltip title="Start a new conversation">
              <span>
                <IconButton size="small" onClick={resetConversation} disabled={loading}>
                  <ResetIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton size="small" onClick={closeAssistant}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
        {messages.length === 0 && !loading ? (
          <Box sx={{ m: 'auto', textAlign: 'center', color: 'text.secondary', px: 2 }}>
            <RobotIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">
              Ask a question or describe a problem, and I'll point you to the right AskIAM path — or the
              Global Helpdesk if none fits.
            </Typography>
          </Box>
        ) : (
          <ConversationThread
            messages={messages}
            loading={loading}
            onNavigate={closeAssistant}
            autoScroll={open}
            onFeedback={sendFeedback}
            onStartCollect={collectEnabled && collectStatus === 'idle' ? startCollect : undefined}
            onConfirmCollect={handleConfirmCollect}
            onRefreshCollect={(values) => { void refreshCollectReview(values); }}
            onCancelCollect={cancelCollect}
            collectionActive={collectStatus === 'review' || collectStatus === 'handoff'}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder={inputPlaceholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          inputProps={{ maxLength: 4000, 'aria-label': 'Message AskIAM' }}
          disabled={awaitingCardAction}
          autoFocus
        />
        <Button
          variant="contained"
          onClick={submit}
          disabled={loading || awaitingCardAction || !input.trim()}
          sx={{ minWidth: 0, px: 2 }}
        >
          <SendIcon fontSize="small" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssistantChatModal;
